import React, { useEffect, useRef, useState } from 'react';
import { Geo } from '../engine/geometry';
import { drawStation } from '../engine/draw';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';

type ViewStyle = 'schematic' | 'engineering' | 'fea';

interface Props {
  geoList: Geo[];
  partName?: string;
  wire?: number;
  blank?: number;
  template?: string;
  machine?: string;
}

const STYLE_LABELS: Record<ViewStyle, string> = {
  schematic: 'Schematic',
  engineering: 'Eng. Drawing',
  fea: 'FEA Strain Map',
};

export default function SequenceViewer({ geoList, partName, wire, blank, template, machine }: Props) {
  const [style, setStyle] = useState<ViewStyle>('schematic');
  const [selected, setSelected] = useState<number | null>(1);
  const [playing, setPlaying] = useState(false);
  const playRef = useRef<NodeJS.Timeout | null>(null);

  // Canvas refs per station
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  // Draw all canvases whenever style or data changes
  useEffect(() => {
    geoList.forEach((geo, i) => {
      const c = canvasRefs.current[i];
      if (c) drawStation(c, geo, style, selected === i);
    });
  }, [geoList, style, selected]);

  // Re-draw when selection changes
  useEffect(() => {
    geoList.forEach((geo, i) => {
      const c = canvasRefs.current[i];
      if (c) drawStation(c, geo, style, selected === i);
    });
  }, [selected]);

  // Animation play
  useEffect(() => {
    if (playing) {
      let idx = 0;
      const step = () => {
        setSelected(idx);
        idx = (idx + 1) % geoList.length;
        playRef.current = setTimeout(step, 900);
      };
      step();
    } else {
      if (playRef.current) clearTimeout(playRef.current);
    }
    return () => { if (playRef.current) clearTimeout(playRef.current); };
  }, [playing, geoList.length]);

  const selGeo = selected !== null ? geoList[selected] : null;
  const CANVAS_W = 130;
  const CANVAS_H = 210;

  return (
    <div>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          {partName && <div style={{ fontWeight: 600, color: 'var(--c-navy)', fontSize: 14 }}>{partName}</div>}
          <div style={{ fontSize: 12, color: 'var(--t-muted)', marginTop: 2 }}>
            {template && <span className="badge badge-navy" style={{ marginRight: 6 }}>Template {template}</span>}
            {wire && <span style={{ marginRight: 12 }}>Wire Ø{wire.toFixed(2)}mm</span>}
            {blank && <span style={{ marginRight: 12 }}>Blank {blank.toFixed(1)}mm</span>}
            {machine && <span>{machine}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Style toggle */}
          <div className="style-toggle">
            {(Object.keys(STYLE_LABELS) as ViewStyle[]).map(s => (
              <button key={s} className={`style-toggle-btn ${style === s ? 'active' : ''}`}
                onClick={() => setStyle(s)}>{STYLE_LABELS[s]}</button>
            ))}
          </div>
          {/* Play button */}
          <button className="btn btn-secondary btn-sm"
            onClick={() => setPlaying(!playing)}
            style={{ minWidth: 72 }}>
            {playing ? '⏸ Pause' : '▶ Animate'}
          </button>
        </div>
      </div>

      {/* FEA legend */}
      {style === 'fea' && (
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, padding:'8px 14px', background:'#0f1623', borderRadius:6, fontSize:11, color:'#94a3b8' }}>
          <Info size={12} />
          Colour map: effective strain estimate (Hollomon σ = Kε^n). Blue = low strain · Green = moderate · Red = high.
          <span style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:4 }}>
            {['#1432b4','#00aae6','#1ebe46','#e6d200','#d22314'].map((c,i) => (
              <span key={i} style={{ width:14, height:10, background:c, borderRadius:2, display:'inline-block' }} />
            ))}
            <span style={{ marginLeft:2 }}>Low → High</span>
          </span>
        </div>
      )}

      {/* Station canvases — horizontal scroll */}
      <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0, minWidth: 'max-content', padding: '4px 0 16px' }}>
          {geoList.map((geo, i) => (
            <React.Fragment key={i}>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                <canvas
                  ref={el => { canvasRefs.current[i] = el; }}
                  width={CANVAS_W} height={CANVAS_H}
                  onClick={() => setSelected(i)}
                  style={{
                    cursor: 'pointer',
                    borderRadius: 6,
                    border: selected === i
                      ? '2px solid var(--c-blue)'
                      : style === 'fea' ? '2px solid #1e293b' : '2px solid var(--c-border)',
                    boxShadow: selected === i ? '0 0 0 3px rgba(37,99,235,0.15)' : 'var(--shadow-sm)',
                    transition: 'all 0.15s',
                    background: style === 'fea' ? '#0f1623' : '#fff',
                  }}
                />
                {/* Progress dot */}
                <div style={{
                  width: 7, height: 7, borderRadius: '50%', marginTop: 6,
                  background: selected === i ? 'var(--c-blue)' : 'var(--c-border)',
                  transition: 'background 0.2s',
                }} />
              </div>
              {/* Arrow between stations */}
              {i < geoList.length - 1 && (
                <div style={{
                  display:'flex', alignItems:'center', padding:'0 2px',
                  paddingBottom: 22, flexShrink: 0,
                }}>
                  <svg width="28" height="14" style={{ display:'block' }}>
                    <line x1="2" y1="7" x2="22" y2="7"
                      stroke={style === 'fea' ? '#334155' : '#c8cdd8'} strokeWidth="1.5"/>
                    <path d="M18 3L26 7L18 11"
                      fill="none" stroke={style === 'fea' ? '#334155' : '#c8cdd8'}
                      strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Selected station info panel */}
      {selGeo && (
        <div style={{
          background: style === 'fea' ? '#0f1623' : 'var(--c-surface2)',
          border: `1px solid ${style === 'fea' ? '#1e293b' : 'var(--c-border)'}`,
          borderRadius: 8, padding: '14px 18px', marginTop: 8,
          animation: 'fadeIn 0.15s ease',
        }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 10 }}>
            <div style={{ fontWeight:600, fontSize:14, color: style === 'fea' ? '#e2e8f0' : 'var(--c-navy)' }}>
              {selGeo.label}
            </div>
            <div style={{ display:'flex', gap:6 }}>
              {selGeo.koType && selGeo.koType !== '—' && (
                <span className={`badge ${selGeo.koType?.includes('Active') ? 'badge-green' : selGeo.koType?.includes('None') ? 'badge-gray' : 'badge-blue'}`}>
                  KO: {selGeo.koType}
                </span>
              )}
              {selGeo.punchMat && selGeo.punchMat !== '—' && (
                <span className="badge badge-amber">Insert: {selGeo.punchMat}</span>
              )}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:8 }}>
            {[
              selGeo.headD != null && ['Head Ø', `${selGeo.headD.toFixed(2)}mm`],
              selGeo.shankD != null && ['Shank Ø', `${selGeo.shankD.toFixed(2)}mm`],
              selGeo.nutAF != null && ['A/F', `${selGeo.nutAF.toFixed(2)}mm`],
              selGeo.nutH  != null && ['Height', `${selGeo.nutH.toFixed(2)}mm`],
              selGeo.boreD && selGeo.boreD > 0 && ['Bore Ø', `${selGeo.boreD.toFixed(2)}mm`],
              selGeo.socketAF != null && ['Socket A/F', `${selGeo.socketAF.toFixed(2)}mm`],
              selGeo.bodyD != null && ['Body Ø', `${selGeo.bodyD.toFixed(2)}mm`],
              selGeo.coneAngle != null && ['Cone angle', `${selGeo.coneAngle}°`],
              selGeo.dieAngle != null && selGeo.dieAngle > 0 && ['Die entry', `${selGeo.dieAngle}°`],
              selGeo.strainMax != null && ['Peak strain est.', `${(selGeo.strainMax * 100).toFixed(0)}%`],
              selGeo.palletStr && selGeo.palletStr !== '—' && ['TC Pallet', selGeo.palletStr],
            ].filter(Boolean).map((item, i) => { const [k,v] = item as [string,string]; return (
              <div key={i} style={{
                display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'5px 10px',
                background: style === 'fea' ? '#1e293b' : 'var(--c-surface)',
                borderRadius: 5,
                border: `1px solid ${style === 'fea' ? '#334155' : 'var(--c-border)'}`,
                fontSize:12,
              }}>
                <span style={{ color: style === 'fea' ? '#94a3b8' : 'var(--t-muted)' }}>{k}</span>
                <span className="val-pill"
                  style={style === 'fea' ? { background:'#334155', color:'#93c5fd', borderColor:'#475569' } : {}}>
                  {v}
                </span>
              </div>
            );})}
          </div>
        </div>
      )}
    </div>
  );
}
